import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { LabelPreview } from "../orders/LabelPreview";

type Status = "PAID" | "PROCESSING" | "SHIPPED" | "CANCELLED";

type Collar = {
  id: string;
  publicId: string | null;
  active: boolean;
  type: "collar" | "tag";
  color: string | null;
  showName: boolean;
  showMotto: boolean;
  cat: { id: string; name: string; motto: string | null };
};

// POSIX-sicheres Quoting: in einfache Anführungszeichen, internes ' wird zu '\''
function shellQuote(s: string): string {
  return `'${s.replace(/'/g, "'\\''")}'`;
}

// Erzeugt den print_petlabel.py-Aufruf für ein Halsband; respektiert show-Flag.
function petLabelCmd(c: Collar): string {
  const parts = ["python3 print_petlabel.py", c.publicId ?? c.cat.id];
  if (c.showName && c.cat.name) parts.push(`--name ${shellQuote(c.cat.name)}`);
  return parts.join(" ");
}

function CopyButton({ text, label, className }: { text: string; label: string; className?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* Clipboard nicht verfügbar (z. B. ohne HTTPS) — ignorieren */
    }
  };
  return (
    <button type="button" onClick={copy} className={className}>
      {copied ? "Kopiert ✓" : label}
    </button>
  );
}

function CollarRow({ collar: c }: { collar: Collar }) {
  const [showPreview, setShowPreview] = useState(false);
  return (
    <div className="py-2 text-sm">
      <div className="flex items-center justify-between">
        <div>
          <span className="font-medium">{c.cat.name}</span>{" "}
          <span className="text-slate-500">
            {c.type === "tag" ? "Mini-Anhänger" : `Halsband${c.color ? ` · ${c.color}` : ""}`}
          </span>
          {c.publicId && <code className="ml-2 text-xs text-slate-500">{c.publicId}</code>}
          {!c.active && <span className="ml-2 text-xs font-medium text-amber-600">gesperrt</span>}
          <span className="text-slate-400 ml-2">{c.showName && "Name"}</span>
        </div>
        <div className="flex items-center gap-3">
          <code className="text-xs text-slate-600">
            {window.location.origin}/cat/{c.cat.id}
          </code>
          <CopyButton text={petLabelCmd(c)} label="CMD kopieren" className="text-xs underline" />
          <button
            type="button"
            onClick={() => setShowPreview((v) => !v)}
            className="text-xs underline"
          >
            {showPreview ? "Label-Vorschau ausblenden" : "Label-Vorschau"}
          </button>
        </div>
      </div>
      {showPreview && (
        <div className="mt-2 max-w-[320px]">
          <LabelPreview catId={c.cat.id} name={c.cat.name} showName={c.showName} code={c.publicId} width={320} />
        </div>
      )}
    </div>
  );
}

interface Order {
  id: string;
  status: Status;
  priceCents: number;
  createdAt: string;
  processedAt: string | null;
  shipStreet: string;
  shipZip: string;
  shipCity: string;
  shipCountry: string;
  user: { id: string; email: string; name: string };
  collars: Collar[];
}

const STATUSES: Status[] = ["PAID", "PROCESSING", "SHIPPED", "CANCELLED"];

export function AdminOrdersPage() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status | "">("");
  const q = useQuery({
    queryKey: ["admin-orders", status],
    queryFn: () => api<Order[]>(`/api/admin/orders${status ? `?status=${status}` : ""}`),
  });

  const setOrderStatus = useMutation({
    mutationFn: ({ id, status }: { id: string; status: Status }) =>
      api(`/api/admin/orders/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin-orders"] }),
  });

  return (
    <div>
      <h1 className="font-display text-3xl font-semibold tracking-tight mb-4">Bestellungen</h1>
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setStatus("")}
          className={`btn ${!status ? "bg-slate-900 text-white" : "btn-secondary"}`}
        >
          Alle
        </button>
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`btn ${status === s ? "bg-slate-900 text-white" : "btn-secondary"}`}
          >
            {s}
          </button>
        ))}
      </div>

      <div className="space-y-4">
        {q.data?.map((o) => (
          <div key={o.id} className="card p-4">
            <div className="flex items-start justify-between">
              <div>
                <div className="font-semibold">
                  {o.user.name} <span className="text-slate-500 font-normal">({o.user.email})</span>
                </div>
                <div className="text-sm text-slate-500">
                  {new Date(o.createdAt).toLocaleString()} · {(o.priceCents / 100).toFixed(2)} €
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  Versand: {o.shipStreet}, {o.shipZip} {o.shipCity}, {o.shipCountry}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  value={o.status}
                  onChange={(e) => setOrderStatus.mutate({ id: o.id, status: e.target.value as Status })}
                  className="input w-auto"
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                <CopyButton
                  text={o.collars.map(petLabelCmd).join("\n")}
                  label="CMDs kopieren"
                  className="btn-secondary"
                />
                <a
                  href={`/api/admin/orders/${o.id}/labels.zip`}
                  className="btn-primary"
                  download
                >
                  PNGs (.zip)
                </a>
              </div>
            </div>

            <div className="mt-3 divide-y divide-slate-100">
              {o.collars.map((c) => (
                <CollarRow key={c.id} collar={c} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
